import FlightDetailsFooter from '@/src/components/desktop/srp/flightResults/flightDetails/flightDetailsFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useFlightDetails } from '@/src/hooks/srp/flightDetails'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { getErrorMessage } from '@/src/utils/srp/searchUtils/getErrorMessage'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useMemo } from 'react'
import FlightDetails from './flightDetails'

interface FlightDetailsProps {
    onSelect: () => void
    flights: FlightCard[]
    sectors: Sectors
    jsons: JsonsData
    sid: string
    isRTSolutionView?: boolean
    showBaggage?: boolean
}

const FlightDetailsWrapper: FC<FlightDetailsProps> = ({
    flights,
    sectors,
    jsons,
    onSelect,
    sid,
    isRTSolutionView,
    showBaggage
}) => {
    const {
        baggageData,
        baggageLoading,
        baggageRTKError,
        miniRuleLoading,
        miniRuleRTKError,
        minirule = [],
        prices,
        footerButtonCTA,
        retryCount,
        handleRetryBaggage,
        maxRetries
    } = useFlightDetails({
        flights,
        jsons,
        sectors,
        onSelect,
        sid,
        showBaggage,
        isRTSolutionView
    })
    const { formatDate } = useFormatDate()
    const tFlightDetails = useTranslations('srpFlightDetails')
    const tCommon = useTranslations('common')

    const baggageError = getErrorMessage(baggageRTKError)
    const miniRuleError = getErrorMessage(miniRuleRTKError)
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '90vh',
                maxHeight: '90vh',
                overflow: 'hidden'
            }}
        >
            {/* Header - Fixed at top */}
            <Box
                sx={{
                    p: 2,
                    pb: 1,
                    backgroundColor: 'white',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography sx={{ fontSize: '17px', fontWeight: 600 }}>{tFlightDetails('flightDetails')}</Typography>
            </Box>

            {/* Body - Scrollable */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {flights.map((flight, index) => {
                    const targetSectors = useMemo(
                        () => flight?.sectorKeys?.map(sectorKey => sectors[sectorKey]),
                        [flight, sectors]
                    )
                    return (
                        <Box
                            key={index}
                            sx={{
                                mb: 2,
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none'
                            }}
                        >
                            <ErrorBoundaryWrapper identifier={`flight-details-${index}-PWA`}>
                                <FlightDetails
                                    baggageError={baggageError}
                                    baggageLoading={baggageLoading}
                                    baggageData={baggageData}
                                    jsons={jsons}
                                    targetSectors={targetSectors}
                                    minirule={minirule[index] || {}}
                                    miniRuleLoading={miniRuleLoading}
                                    miniRuleError={miniRuleError}
                                    tFlightDetails={tFlightDetails}
                                    tCommon={tCommon}
                                    formatDate={formatDate}
                                    showBaggage={showBaggage}
                                    retryCount={retryCount}
                                    handleRetryBaggage={handleRetryBaggage}
                                    maxRetries={maxRetries}
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    )
                })}
            </Box>

            {/* Footer - Fixed at bottom */}
            <Box
                sx={{
                    backgroundColor: 'white',
                    flexShrink: 0,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10
                }}
            >
                <ErrorBoundaryWrapper identifier="flight-details-footer-PWA">
                    <FlightDetailsFooter
                        showSelectButton={true}
                        price={prices.price}
                        strikeOffPrice={prices.strikeOffPrice}
                        onSelect={onSelect}
                        buttonCTA={footerButtonCTA}
                    />
                </ErrorBoundaryWrapper>
            </Box>
        </Box>
    )
}

export default FlightDetailsWrapper
